import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Dropdown from 'react-bootstrap/Dropdown';

import useConfig from 'hooks/useConfig';
import { ThemeMode } from 'config';
import { getResolvedTheme } from 'components/setResolvedTheme';
import SimpleBarScroll from 'components/third-party/SimpleBar';
import useAuth from '../../auth/useAuth';
import { getPusher } from 'utils/pusher';
import { useGetNotifications, useReadAllNotifications, useReadNotification } from '../../APIs/Notification/Notification';
import { notify } from '@common/NotificationService';
import './header.css';

/**
 * The header's notification bell and theme switch, shared by the dashboard's
 * Header and the Store Room's own top bar. Both are styled by header.css, which
 * scopes them under a .yz-hdr ancestor.
 */

// The icon beside each notification, by what it is about.
const notificationIcon = (type = '') => {
  const t = String(type).toLowerCase();
  if (t.startsWith('booking')) return 'ph-calendar-check';
  if (t.startsWith('giftcard')) return 'ph-gift';
  if (t.startsWith('stock_request')) return 'ph-package';
  return 'ph-bell';
};

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { notifications: apiNotifications } = useGetNotifications();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const { readAll } = useReadAllNotifications();
  const { readOne } = useReadNotification();
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (!apiNotifications.length) return;

    const unread = apiNotifications.filter((n) => !n.is_read);

    setNotifications(unread);
    setUnreadCount(unread.length);
  }, [apiNotifications.length]);

  useEffect(() => {
    if (!user?.user?.id) return;

    const pusher = getPusher();
    const channelName = `user.${user.user.id}`;
    const channel = pusher.subscribe(channelName);

    const handleEvent = (payload) => {
      setNotifications((prev) => {
        // prevent duplicates
        if (prev.some((n) => n.id === payload.id)) return prev;
        return [payload, ...prev];
      });

      setUnreadCount((prev) => prev + 1);
    };

    channel.bind('booking.notification', handleEvent);

    return () => {
      channel.unbind('booking.notification', handleEvent);
      pusher.unsubscribe(channelName);
    };
  }, [user?.user?.id]);

  const handleNotificationClick = async (notification) => {
    try {
      await readOne(notification.id);
    } catch {
      // ignore read failure for UX
    }

    // remove from unread dropdown
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    setUnreadCount((prev) => Math.max(prev - 1, 0));
    setShowNotifications(false);

    // Each kind opens the page it is about. Absolute paths, so a click from the
    // Store Room (outside the dashboard's routes) lands in the same place.
    const type = notification.type?.toLowerCase() || '';
    if (type.startsWith('booking')) {
      navigate('/booking-management', {
        state: {
          bookingId: notification.booking_id,
          openView: true
        }
      });
    } else if (type.startsWith('giftcard')) {
      navigate('/gift_card');
    } else if (type.startsWith('stock_request')) {
      navigate('/store-room?tab=requests');
    }
  };

  const handleReadAll = async (e) => {
    e.preventDefault();

    try {
      const res = await readAll();

      if (res?.success) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        notify.error(res?.message || 'Failed to mark notifications as read', '');
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Something went wrong while marking notifications as read';

      notify.error(errorMessage, '');
    }
  };

  return (
    <Dropdown align="end" show={showNotifications} onToggle={(isOpen) => setShowNotifications(isOpen)}>
      <Dropdown.Toggle as="button" type="button" className="yz-hdr-btn arrow-none" id="notification-dropdown" aria-label="Notifications">
        <i className="ph ph-bell" />
        {unreadCount > 0 && <span className="yz-hdr-count">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </Dropdown.Toggle>

      <Dropdown.Menu className="yz-hdr-menu yz-hdr-notifs">
        <div className="yz-hdr-notifs-head">
          <span className="yz-hdr-notifs-title">Notifications</span>
          <div className="yz-hdr-notifs-links">
            {notifications.length > 0 && (
              <Link to="#" onClick={handleReadAll}>
                Mark all read
              </Link>
            )}
            <Link to="/notifications" onClick={() => setShowNotifications(false)}>
              View all
            </Link>
          </div>
        </div>

        {notifications.length === 0 ? (
          <div className="yz-hdr-notifs-empty">
            <span className="yz-hdr-notifs-empty-icon">
              <i className="ph ph-check-circle" />
            </span>
            You&apos;re all caught up
          </div>
        ) : (
          <SimpleBarScroll style={{ maxHeight: 'calc(100vh - 215px)' }}>
            <div className="yz-hdr-notifs-list">
              {notifications.map((notification) => (
                <button key={notification?.id} type="button" className="yz-hdr-notif" onClick={() => handleNotificationClick(notification)}>
                  <span className="yz-hdr-notif-icon">
                    <i className={`ph ${notificationIcon(notification?.type)}`} />
                  </span>
                  <span className="yz-hdr-notif-body">
                    <span className="yz-hdr-notif-top">
                      <span className="yz-hdr-notif-title">{notification?.title}</span>
                      <span className="yz-hdr-notif-time">{notification?.created_at}</span>
                    </span>
                    <span className="yz-hdr-notif-msg">{notification?.message}</span>
                  </span>
                  <span className="yz-hdr-notif-dot" aria-label="Unread" />
                </button>
              ))}
            </div>
          </SimpleBarScroll>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

/** One click switches between Light and Dark. */
export function ThemeToggle() {
  const { onChangeMode, mode } = useConfig();

  // Two themes only. A saved "Default" (follow the device) shows as whichever
  // of the two it currently resolves to, and the button switches to the other.
  const activeTheme = getResolvedTheme(mode);
  const themeIcon = activeTheme === ThemeMode.DARK ? 'ph-moon' : 'ph-sun';

  return (
    <button
      type="button"
      className="yz-hdr-btn"
      onClick={() => onChangeMode(activeTheme === ThemeMode.DARK ? ThemeMode.LIGHT : ThemeMode.DARK)}
      aria-label={`Switch to ${activeTheme === ThemeMode.DARK ? 'light' : 'dark'} mode`}
      title={`Switch to ${activeTheme === ThemeMode.DARK ? 'light' : 'dark'} mode`}
    >
      {/* Keyed by the theme so the new icon spins in on a change. */}
      <i key={themeIcon} className={`ph ${themeIcon} yz-hdr-theme-icon`} />
    </button>
  );
}
